// FINAL PDF EXPORT VERSION — src/pages/timetable/MasterTimetable.jsx
// Supports multiple named timetable groups, each containing multiple classes.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDaysIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import apiClient from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { FireLoader } from "../../components/Loader";
import {
  showConfirm,
  showError,
  showSuccess,
  showWarning,
} from "../../utils/alert";

const unwrapList = (response, keys = []) => {
  const value = response?.data?.data ?? response?.data ?? [];
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return value?.rows || [];
};

const teacherLabel = (teacher) => {
  if (!teacher) return "No teacher";
  const name = [teacher.first_name, teacher.last_name]
    .filter(Boolean)
    .join(" ");
  const code = teacher.employee_number || String(teacher.id).padStart(2, "0");
  return `${name || "Teacher"} (${code})`;
};

const isBreakOrLunch = (period) =>
  ["break", "lunch"].includes(String(period?.period_type || "").toLowerCase());

const isTeachingPeriod = (period) => {
  const value = period?.is_teaching_period;
  const enabled = value === true || value === 1 || value === "1";

  return (
    enabled &&
    String(period?.period_type || "lesson").toLowerCase() === "lesson"
  );
};

// Academic class order: Nursery -> Primary -> JSS -> SSS. Convert Roman
// numerals so Class IV correctly comes before Class V and Class VI.
const LEVEL_ORDER = {
  nursery: 0,
  primary: 1,
  jss: 2,
  junior: 2,
  sss: 3,
  senior: 3,
};

const ROMAN_VALUES = { I: 1, V: 5, X: 10 };

const romanToNumber = (value) => {
  const roman = String(value || "").toUpperCase();
  if (!roman || !/^[IVX]+$/.test(roman)) return null;
  let total = 0;
  let previous = 0;
  for (let index = roman.length - 1; index >= 0; index -= 1) {
    const current = ROMAN_VALUES[roman[index]] || 0;
    if (current < previous) total -= current;
    else {
      total += current;
      previous = current;
    }
  }
  return total || null;
};

const getClassRecord = (item) => item?.class || item || {};

const getLevelRank = (item) => {
  const klass = getClassRecord(item);
  const level = String(klass.level || "")
    .trim()
    .toLowerCase();
  if (Object.prototype.hasOwnProperty.call(LEVEL_ORDER, level)) {
    return LEVEL_ORDER[level];
  }
  const name = String(klass.name || item?.title || "").toLowerCase();
  if (/nursery|pre[ -]?primary|kindergarten|\bkg\b/.test(name)) return 0;
  if (/\bjss\b|junior secondary/.test(name)) return 2;
  if (/\bsss\b|senior secondary/.test(name)) return 3;
  if (/\bclass\b|primary/.test(name)) return 1;
  return 99;
};

const getClassNumber = (item) => {
  const klass = getClassRecord(item);
  const name = String(klass.name || item?.title || "")
    .trim()
    .toUpperCase();
  const match = name.match(
    /(?:NURSERY|PRIMARY|CLASS|JSS|SSS|GRADE|YEAR)\s*[-:]?\s*(\d+|[IVX]+)/,
  );
  const raw = match?.[1] || name.match(/\b(\d+|[IVX]+)\b/)?.[1];
  if (!raw) return Number.MAX_SAFE_INTEGER;
  if (/^\d+$/.test(raw)) return Number(raw);
  return romanToNumber(raw) ?? Number.MAX_SAFE_INTEGER;
};

const classCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const compareClassesAcademically = (a, b) => {
  const levelDifference = getLevelRank(a) - getLevelRank(b);
  if (levelDifference !== 0) return levelDifference;
  const numberDifference = getClassNumber(a) - getClassNumber(b);
  if (numberDifference !== 0) return numberDifference;
  const aClass = getClassRecord(a);
  const bClass = getClassRecord(b);
  return classCollator.compare(
    String(aClass.name || a?.title || ""),
    String(bClass.name || b?.title || ""),
  );
};

const sortClassesAcademically = (items) =>
  [...items].sort(compareClassesAcademically);

const blankLesson = {
  timetableId: "",
  entryId: null,
  day_of_week: "",
  period_id: "",
  subject_id: "",
  teacher_id: "",
  room: "",
  remarks: "",
};

export default function MasterTimetable() {
  const { user, planAccess } = useAuth();
  const isStarterPlan =
    String(planAccess?.plan?.name || "").trim().toLowerCase() === "starter";
  const role = String(user?.role || "").toLowerCase();
  const canEdit = ["admin", "school_admin", "super_admin"].includes(role);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [schoolName, setSchoolName] = useState("School");
  const [periods, setPeriods] = useState([]);
  const [timetables, setTimetables] = useState([]);
  const [classes, setClasses] = useState([]);
  const [years, setYears] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teachingDays, setTeachingDays] = useState([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ]);
  const [selectedYearId, setSelectedYearId] = useState("");
  const [selectedGroupUuid, setSelectedGroupUuid] = useState("");
  const [ribbonTab, setRibbonTab] = useState("home");
  const [showCreate, setShowCreate] = useState(false);
  const [lesson, setLesson] = useState(blankLesson);
  const [createForm, setCreateForm] = useState({
    academic_year_id: "",
    group_name: "",
    class_ids: [],
    description: "",
  });

  const selectRibbonTab = (tab) => {
    setRibbonTab(tab);
    if (tab !== "home") {
      setShowCreate(false);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const isTeacher = role === "teacher";

      const responses = await Promise.all([
        apiClient.get("/timetables/settings"),
        apiClient.get("/timetables/periods"),
        apiClient.get("/timetables/master"),
        isTeacher
          ? apiClient.get("/teachers/me/classes")
          : apiClient.get("/classes", { params: { page: 1, limit: 500 } }),
        apiClient.get("/academic-years"),
        apiClient.get("/subjects", { params: { page: 1, limit: 500 } }),
        isTeacher
          ? Promise.resolve({ data: { data: [] } })
          : apiClient.get("/teachers", { params: { page: 1, limit: 500 } }),
        apiClient.get("/schools/profile").catch(() => null),
      ]);

      const [
        settingsResponse,
        periodsResponse,
        timetablesResponse,
        classesResponse,
        yearsResponse,
        subjectsResponse,
        teachersResponse,
        schoolProfileResponse,
      ] = responses;

      const assignedClassRows = sortClassesAcademically(
        unwrapList(classesResponse, ["classes"]),
      );
      const assignedClassIds = new Set(
        assignedClassRows
          .map((klass) => Number(klass.id))
          .filter((id) => Number.isInteger(id) && id > 0),
      );
      const allTimetableRows = unwrapList(timetablesResponse, ["timetables"]);
      const timetableRows = isTeacher
        ? allTimetableRows.filter((timetable) =>
            assignedClassIds.has(Number(timetable.class_id || timetable.class?.id)),
          )
        : allTimetableRows;
      const yearRows = unwrapList(yearsResponse, ["academicYears", "years"]);
      const configuredDays = settingsResponse?.data?.data?.teaching_days;
      const schoolProfile =
        schoolProfileResponse?.data?.data ||
        schoolProfileResponse?.data?.school ||
        schoolProfileResponse?.data ||
        {};
      const resolvedSchoolName =
        schoolProfile.schoolName ||
        schoolProfile.name ||
        user?.school?.schoolName ||
        user?.school?.name ||
        user?.school_name;

      if (resolvedSchoolName) setSchoolName(resolvedSchoolName);

      setTeachingDays(
        Array.isArray(configuredDays) && configuredDays.length
          ? configuredDays
          : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      );
      setPeriods(unwrapList(periodsResponse, ["periods"]));
      setTimetables(timetableRows);
      setClasses(assignedClassRows);
      setYears(yearRows);
      setSubjects(unwrapList(subjectsResponse, ["subjects"]));
      setTeachers(unwrapList(teachersResponse, ["teachers"]));

      const currentYear = yearRows.find((year) => year.is_current);
      setSelectedYearId(
        (value) =>
          value ||
          String(currentYear?.id || timetableRows[0]?.academic_year_id || ""),
      );
    } catch (error) {
      console.error("Failed to load master timetable:", error);
      await showError({
        title: "Unable to Load Timetable",
        text:
          error?.response?.data?.message ||
          "The timetable information could not be loaded.",
      });
    } finally {
      setLoading(false);
    }
  }, [role, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredTimetables = useMemo(
    () =>
      timetables
        .filter(
          (item) =>
            !selectedYearId ||
            String(item.academic_year_id) === String(selectedYearId),
        )
        .sort(compareClassesAcademically),
    [timetables, selectedYearId],
  );

  const timetableGroups = useMemo(() => {
    const groups = new Map();

    filteredTimetables.forEach((timetable) => {
      const groupUuid = timetable.group_uuid || `legacy-${timetable.id}`;
      if (!groups.has(groupUuid)) {
        groups.set(groupUuid, {
          group_uuid: groupUuid,
          group_name:
            timetable.group_name || timetable.title || "School Timetable",
          academic_year_id: timetable.academic_year_id,
          timetables: [],
        });
      }
      groups.get(groupUuid).timetables.push(timetable);
    });

    return Array.from(groups.values()).map((group) => {
      // A timetable group is allowed only one timetable per class. Defensively
      // remove duplicated class rows returned by older/legacy data.
      const seenClasses = new Set();
      const uniqueTimetables = group.timetables.filter((timetable) => {
        const classKey = String(
          timetable.class_id ||
            timetable.class?.id ||
            `timetable-${timetable.id}`,
        );
        if (seenClasses.has(classKey)) return false;
        seenClasses.add(classKey);
        return true;
      });

      return {
        ...group,
        timetables: uniqueTimetables.sort(compareClassesAcademically),
      };
    });
  }, [filteredTimetables]);

  useEffect(() => {
    if (!timetableGroups.length) {
      setSelectedGroupUuid("");
      return;
    }

    const selectedStillExists = timetableGroups.some(
      (group) => group.group_uuid === selectedGroupUuid,
    );
    if (!selectedStillExists) {
      setSelectedGroupUuid(timetableGroups[0].group_uuid);
    }
  }, [timetableGroups, selectedGroupUuid]);

  const selectedGroup = useMemo(
    () =>
      timetableGroups.find((group) => group.group_uuid === selectedGroupUuid) ||
      null,
    [timetableGroups, selectedGroupUuid],
  );

  const displayedTimetables = selectedGroup?.timetables || [];

  const sortedPeriods = useMemo(
    () =>
      [...periods].sort(
        (a, b) => Number(a.period_number) - Number(b.period_number),
      ),
    [periods],
  );

  const timetableRowCount = teachingDays.length * displayedTimetables.length;

  const entryMap = useMemo(() => {
    const map = new Map();
    displayedTimetables.forEach((timetable) => {
      (timetable.entries || []).forEach((entry) => {
        map.set(
          `${timetable.id}:${entry.day_of_week}:${entry.period_id}`,
          entry,
        );
      });
    });
    return map;
  }, [displayedTimetables]);

  const openLesson = (timetable, day, period) => {
    if (!canEdit || !isTeachingPeriod(period)) return;
    const entry = entryMap.get(`${timetable.id}:${day}:${period.id}`);
    setLesson({
      timetableId: timetable.id,
      entryId: entry?.id || null,
      day_of_week: day,
      period_id: String(period.id),
      subject_id: entry?.subject_id ? String(entry.subject_id) : "",
      teacher_id: entry?.teacher_id ? String(entry.teacher_id) : "",
      room: entry?.room || "",
      remarks: entry?.remarks || "",
    });
  };

  const saveLesson = async (event) => {
    event.preventDefault();

    if (!lesson.subject_id) {
      await showWarning({
        title: "Subject Required",
        text: "Please select the subject for this lesson.",
      });
      return;
    }

    if (!lesson.teacher_id) {
      await showWarning({
        title: "Teacher Required",
        text: "Please select the teacher responsible for this lesson.",
      });
      return;
    }

    setSaving(true);
    try {
      await apiClient.put(`/timetables/${lesson.timetableId}/entries`, {
        day_of_week: lesson.day_of_week,
        period_id: Number(lesson.period_id),
        subject_id: lesson.subject_id ? Number(lesson.subject_id) : null,
        teacher_id: lesson.teacher_id ? Number(lesson.teacher_id) : null,
        room: lesson.room.trim() || null,
        remarks: lesson.remarks.trim() || null,
      });
      setLesson(blankLesson);
      await loadData();
      await showSuccess({
        title: "Lesson Saved",
        text: "The subject and teacher were added to the timetable.",
      });
    } catch (error) {
      await showError({
        title: "Unable to Save Lesson",
        text: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteLesson = async () => {
    if (!lesson.entryId) return;

    const confirmed = await showConfirm({
      title: "Remove Lesson",
      text: "Are you sure you want to remove this lesson from the timetable?",
      confirmText: "Yes, remove lesson",
      icon: "warning",
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      await apiClient.delete(
        `/timetables/${lesson.timetableId}/entries/${lesson.entryId}`,
      );
      setLesson(blankLesson);
      await loadData();
      await showSuccess({
        title: "Lesson Removed",
        text: "The lesson was removed from the timetable.",
      });
    } catch (error) {
      await showError({
        title: "Unable to Remove Lesson",
        text: error?.response?.data?.message || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const createTimetable = async (event) => {
    event.preventDefault();

    if (!createForm.group_name.trim()) {
      await showWarning({
        title: "Timetable Name Required",
        text: "Enter a clear name such as Upper Primary Timetable.",
      });
      return;
    }

    if (!createForm.academic_year_id) {
      await showWarning({
        title: "Academic Year Required",
        text: "Select the academic year for this timetable.",
      });
      return;
    }

    if (!createForm.class_ids.length) {
      await showWarning({
        title: "Class Required",
        text: "Select at least one class for this timetable.",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await apiClient.post("/timetables", createForm);
      const createdGroupUuid = response?.data?.data?.group_uuid || "";
      await showSuccess({ text: "Timetable created successfully" });
      setShowCreate(false);
      setCreateForm({
        academic_year_id: "",
        group_name: "",
        class_ids: [],
        description: "",
      });
      await loadData();
      if (createdGroupUuid) setSelectedGroupUuid(createdGroupUuid);
    } catch (error) {
      await showError({
        text: error?.response?.data?.message || "Unable to create timetable",
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleCreateClass = (classId) => {
    const value = String(classId);
    setCreateForm((current) => ({
      ...current,
      class_ids: current.class_ids.includes(value)
        ? current.class_ids.filter((id) => id !== value)
        : [...current.class_ids, value],
    }));
  };

  const deleteTimetableGroup = async (group) => {
    if (!canEdit || !group || String(group.group_uuid).startsWith("legacy-")) {
      return;
    }

    const confirmed = await showConfirm({
      title: "Delete Timetable",
      text: `Delete “${group.group_name}” for ${group.timetables.length} class${
        group.timetables.length === 1 ? "" : "es"
      }? All lessons saved inside it will also be deleted.`,
      confirmText: "Yes, delete timetable",
      icon: "error",
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      await apiClient.delete(`/timetables/groups/${group.group_uuid}`);
      setSelectedGroupUuid("");
      await loadData();
      await showSuccess({ text: "Timetable deleted successfully" });
    } catch (error) {
      await showError({
        text: error?.response?.data?.message || "Unable to delete timetable",
      });
    } finally {
      setSaving(false);
    }
  };

  const downloadSelectedTimetablePdf = async () => {
    if (isStarterPlan) {
      return showWarning({
        title: "PDF Download Not Available",
        text: "PDF downloads are not included in the Starter plan. Upgrade to Basic or a higher plan to download PDFs.",
      });
    }

    if (!selectedGroup || !displayedTimetables.length) {
      await showWarning({
        title: "Select a Timetable",
        text: "Choose the specific timetable you want to download.",
      });
      return;
    }

    if (!sortedPeriods.length) {
      await showWarning({
        title: "No Periods Configured",
        text: "Configure the school periods before downloading the timetable.",
      });
      return;
    }

    setDownloadingPdf(true);

    try {
      const selectedYear = years.find(
        (year) => String(year.id) === String(selectedGroup.academic_year_id),
      );
      const document = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageWidth = document.internal.pageSize.getWidth();
      const pageHeight = document.internal.pageSize.getHeight();
      const horizontalMargin = 7;
      // Keep the day compact and give longer class names more room.
      // DAY (17 mm) + CLASS (30 mm).
      const fixedColumnWidth = 47;
      const availablePeriodWidth =
        pageWidth - horizontalMargin * 2 - fixedColumnWidth;
      const periodColumnWidth = availablePeriodWidth / sortedPeriods.length;
      const tableFontSize =
        sortedPeriods.length >= 11 ? 4.7 : sortedPeriods.length >= 9 ? 5.2 : 6;

      const periodKey = (period) => `period_${period.id}`;
      const mergedPeriodKeys = new Set(
        sortedPeriods.filter(isBreakOrLunch).map(periodKey),
      );

      const columns = [
        { header: "DAY", dataKey: "day" },
        { header: "CLASS", dataKey: "class_name" },
        ...sortedPeriods.map((period) => {
          const name = isBreakOrLunch(period)
            ? String(period.period_type).toUpperCase()
            : period.name;
          return {
            header: `${name}\n${String(period.start_time).slice(0, 5)}–${String(
              period.end_time,
            ).slice(0, 5)}`,
            dataKey: periodKey(period),
          };
        }),
      ];

      const body = [];
      teachingDays.forEach((day) => {
        displayedTimetables.forEach((timetable, classIndex) => {
          const row = {
            class_name: timetable.class?.name || timetable.title,
          };

          // Match the physical school timetable: one large day cell with all
          // selected classes listed once underneath that day.
          if (classIndex === 0) {
            row.day = {
              content: "",
              dayLabel: day.toUpperCase(),
              rowSpan: displayedTimetables.length,
              styles: {
                fillColor: [242, 245, 248],
                fontStyle: "bold",
                halign: "center",
                valign: "middle",
              },
            };
          }

          sortedPeriods.forEach((period) => {
            const key = periodKey(period);

            // One break/lunch block for all classes belonging to this day.
            if (isBreakOrLunch(period)) {
              if (classIndex === 0) {
                row[key] = {
                  content: "",
                  periodLabel: String(period.period_type).toUpperCase(),
                  rowSpan: displayedTimetables.length,
                  styles: {
                    fillColor: [242, 245, 248],
                    fontStyle: "bold",
                    halign: "center",
                    valign: "middle",
                  },
                };
              }
              return;
            }

            if (!isTeachingPeriod(period)) {
              row[key] = String(
                period.name || period.period_type || "",
              ).toUpperCase();
              return;
            }

            const entry = entryMap.get(`${timetable.id}:${day}:${period.id}`);
            if (!entry) {
              row[key] = "—";
              return;
            }

            const subjectName = entry.subject?.name || "Lesson";
            const assignedTeacher = teacherLabel(entry.teacher);
            row[key] = `${subjectName}\n${assignedTeacher}`;
          });

          body.push(row);
        });
      });

      const columnStyles = {
        day: {
          cellWidth: 17,
          fontStyle: "bold",
          fillColor: [242, 245, 248],
          textColor: [26, 58, 92],
          halign: "center",
          valign: "middle",
        },
        class_name: {
          cellWidth: 30,
          fontStyle: "bold",
          valign: "middle",
        },
      };

      sortedPeriods.forEach((period) => {
        columnStyles[periodKey(period)] = {
          cellWidth: periodColumnWidth,
          halign: "center",
          valign: "middle",
          ...(isBreakOrLunch(period)
            ? {
                fillColor: [242, 245, 248],
                textColor: [26, 58, 92],
                fontStyle: "bold",
              }
            : {}),
        };
      });

      const timetableTitle = selectedGroup.group_name || "Master Timetable";
      const academicLabel = selectedYear?.name || "Academic Year";

      document.setProperties({
        title: `${schoolName} - ${timetableTitle}`,
        subject: academicLabel || "School master timetable",
        author: schoolName,
        creator: "PawaSchool",
      });

      autoTable(document, {
        columns,
        body,
        startY: 30,
        margin: {
          top: 30,
          right: horizontalMargin,
          bottom: 14,
          left: horizontalMargin,
        },
        theme: "grid",
        showHead: "everyPage",
        styles: {
          font: "helvetica",
          fontSize: tableFontSize,
          cellPadding: 1.2,
          lineColor: [232, 232, 232],
          lineWidth: 0.18,
          overflow: "linebreak",
          minCellHeight: 10.5,
        },
        headStyles: {
          fillColor: [26, 58, 92],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: Math.max(5, tableFontSize),
          halign: "center",
          valign: "middle",
          minCellHeight: 12,
        },
        alternateRowStyles: {
          fillColor: [250, 250, 250],
        },
        // Never split one physical class row between two pages.
        rowPageBreak: "avoid",
        columnStyles,
        didDrawCell: (data) => {
          if (data.section !== "body") return;

          const key = data.column.dataKey;
          const isVerticalDay = key === "day";
          const isVerticalBreak = mergedPeriodKeys.has(key);
          if (!isVerticalDay && !isVerticalBreak) return;

          const rawCell = data.cell.raw;
          const rawValue =
            rawCell && typeof rawCell === "object"
              ? isVerticalDay
                ? rawCell.dayLabel
                : rawCell.periodLabel
              : rawCell;
          if (!rawValue) return;

          const label = String(rawValue).toUpperCase();
          const cellCenterX = data.cell.x + data.cell.width / 2;
          const cellCenterY = data.cell.y + data.cell.height / 2;
          const maximumLabelLength = Math.max(4, data.cell.height - 4);
          let fittedFontSize = isVerticalDay ? 7.5 : 8.5;

          document.saveGraphicsState();
          document.setFont("helvetica", "bold");

          if (isVerticalDay) {
            // Keep long day names centred in the tall merged day cell.
            const stackedDay = label.split("").join("\n");
            const availableHeight = Math.max(8, data.cell.height - 4);
            const lineCount = Math.max(1, label.length);
            const lineHeightFactor = 0.82;
            const pointsPerMillimetre = 2.83465;
            const maximumDayFontSize =
              (availableHeight * pointsPerMillimetre) /
              (lineCount * lineHeightFactor);
            const dayFontSize = Math.max(
              4.5,
              Math.min(7.5, maximumDayFontSize),
            );
            const dayLineHeight =
              (dayFontSize / pointsPerMillimetre) * lineHeightFactor;
            const dayStartY =
              cellCenterY - ((lineCount - 1) * dayLineHeight) / 2;

            document.setFontSize(dayFontSize);
            document.setTextColor(26, 58, 92);
            document.text(stackedDay, cellCenterX, dayStartY, {
              align: "center",
              baseline: "middle",
              lineHeightFactor,
            });
            document.restoreGraphicsState();
            return;
          }

          // Fit BREAK or LUNCH safely inside its merged vertical column.
          document.setFontSize(fittedFontSize);
          const measuredLabelLength = document.getTextWidth(label);
          if (measuredLabelLength > maximumLabelLength) {
            fittedFontSize = Math.max(
              4.5,
              fittedFontSize * (maximumLabelLength / measuredLabelLength),
            );
            document.setFontSize(fittedFontSize);
          }

          document.setTextColor(26, 58, 92);
          document.text(label, cellCenterX, cellCenterY, {
            align: "center",
            baseline: "middle",
            angle: 90,
          });
          document.restoreGraphicsState();
        },
        didDrawPage: (data) => {
          document.setTextColor(26, 26, 26);
          document.setFont("helvetica", "bold");
          document.setFontSize(14);
          document.text(schoolName || "School", pageWidth / 2, 9, {
            align: "center",
          });

          document.setFontSize(11);
          document.setTextColor(26, 58, 92);
          document.text(timetableTitle, pageWidth / 2, 16, {
            align: "center",
          });

          document.setFont("helvetica", "normal");
          document.setFontSize(7.5);
          document.setTextColor(100, 116, 139);
          document.text(
            academicLabel || "Master Timetable",
            pageWidth / 2,
            22,
            { align: "center" },
          );
          document.text(
            `Generated ${new Date().toLocaleDateString()}`,
            pageWidth - horizontalMargin,
            23,
            { align: "right" },
          );

          document.setDrawColor(26, 58, 92);
          document.line(
            horizontalMargin,
            25.5,
            pageWidth - horizontalMargin,
            25.5,
          );

          // Branded ownership footer.
          document.setDrawColor(226, 232, 240);
          document.setLineWidth(0.15);
          document.line(
            horizontalMargin,
            pageHeight - 10,
            pageWidth - horizontalMargin,
            pageHeight - 10,
          );

          document.setFont("helvetica", "normal");
          document.setFontSize(7.2);
          document.setTextColor(26, 58, 92);
          document.text(
            "This document was generated using the PawaSchool Management System.",
            pageWidth / 2,
            pageHeight - 5,
            { align: "center" },
          );

          document.setFontSize(7);
          document.setTextColor(148, 163, 184);
          document.text(
            `Page ${data.pageNumber}`,
            pageWidth - horizontalMargin,
            pageHeight - 5,
            { align: "right" },
          );
        },
      });

      const safeName = `${schoolName}-${timetableTitle}`
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      document.save(`${safeName || "master-timetable"}.pdf`);

      await showSuccess({
        title: "Timetable Downloaded",
        text: `${timetableTitle} is downloaded successfully.`,
      });
    } catch (error) {
      console.error("Timetable PDF error:", error);
      await showError({
        title: "Unable to Download PDF",
        text: "The timetable could not be generated. Please try again.",
      });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return <FireLoader fullScreen size="lg" text="Loading..." />;
  }

  return (
    <div className="mx-auto max-w-[1800px] space-y-3 px-2 pb-8 text-xs sm:space-y-4 sm:px-3 lg:px-5">
      <header className="sticky top-0 z-40 overflow-hidden rounded-2xl border border-gray-200 bg-white/95 shadow-sm backdrop-blur-xl">
        <div className="flex items-center justify-between gap-2 px-3 py-2.5 sm:px-4">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white shadow-sm">
              <CalendarDaysIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-gray-900 sm:text-lg">
                Master Timetable
              </h1>
              <p className="hidden truncate text-[10px] text-gray-500 sm:block sm:text-[11px]">
                Select a cell to add or edit a lesson
              </p>
            </div>
          </div>

          <span className="hidden rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700 lg:inline-flex">
            {canEdit ? "Editing enabled" : "View only"}
          </span>
        </div>

        <nav
          className="flex items-center gap-1 border-t border-gray-100 px-3 pt-1"
          aria-label="Timetable editor tools"
        >
          {[
            { id: "home", label: "Home" },
            { id: "export", label: "Export" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectRibbonTab(tab.id)}
              className={`relative px-2.5 py-1.5 text-[10px] font-semibold transition sm:text-xs ${
                ribbonTab === tab.id
                  ? "text-primary-700"
                  : "text-gray-500 hover:text-gray-800"
              }`}
            >
              {tab.label}
              {ribbonTab === tab.id && (
                <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary-600" />
              )}
            </button>
          ))}
        </nav>

        <div className="min-h-[54px] border-t border-gray-100 bg-gray-50/80 px-2.5 py-2">
          <div className="overflow-x-auto overscroll-x-contain">
            {ribbonTab === "home" && (
              <div className="flex min-w-max items-end gap-2">
                <label className="block">
                  <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Year
                  </span>
                  <select
                    value={selectedYearId}
                    onChange={(event) => setSelectedYearId(event.target.value)}
                    className="h-8 w-[145px] rounded-lg border border-gray-200 bg-white px-2.5 text-[10px] font-medium text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 sm:w-[165px] sm:text-xs"
                  >
                    <option value="">All academic years</option>
                    {years.map((year) => (
                      <option key={year.id} value={year.id}>
                        {year.name}
                      </option>
                    ))}
                  </select>
                </label>

                {!!timetableGroups.length && (
                  <label className="block">
                    <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                      Timetable
                    </span>
                    <select
                      value={selectedGroupUuid}
                      onChange={(event) =>
                        setSelectedGroupUuid(event.target.value)
                      }
                      className="h-8 w-[175px] rounded-lg border border-gray-200 bg-white px-2.5 text-[10px] font-semibold text-gray-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 sm:w-[230px] sm:text-xs"
                    >
                      {timetableGroups.map((group) => (
                        <option key={group.group_uuid} value={group.group_uuid}>
                          {group.group_name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setShowCreate((value) => !value)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-[10px] font-semibold text-white shadow-sm transition hover:bg-primary-700 sm:text-xs"
                  >
                    <PlusIcon className="h-4 w-4" /> New timetable
                  </button>
                )}
              </div>
            )}

            {ribbonTab === "export" && (
              <div className="flex min-w-max items-end gap-2">
                <button
                  type="button"
                  disabled={!selectedGroup || downloadingPdf}
                  onClick={downloadSelectedTimetablePdf}
                  className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary-600 px-3 text-left text-white shadow-sm transition hover:bg-primary-700 disabled:opacity-50"
                >
                  {downloadingPdf ? (
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  ) : (
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  )}
                  <span>
                    <span className="block text-[10px] font-bold sm:text-xs">
                      Download
                    </span>
                    <span className="block text-[10px] text-primary-100">
                      Save this timetable
                    </span>
                  </span>
                </button>
                {canEdit && selectedGroup && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => deleteTimetableGroup(selectedGroup)}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-100 bg-white px-3 text-left text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                  >
                    <TrashIcon className="h-5 w-5" />
                    <span>
                      <span className="block text-[10px] font-bold sm:text-xs">
                        Delete
                      </span>
                      <span className="block text-[10px] text-red-400">
                        Remove this timetable
                      </span>
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {showCreate && canEdit && (
        <form
          onSubmit={createTimetable}
          className="space-y-4 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm transition-all duration-300 sm:p-4"
        >
          <div>
            <h2 className="font-semibold text-gray-800">Create a timetable</h2>
            <p className="mt-1 text-[11px] text-gray-500 sm:text-xs">
              Give it a name and select every class that should appear in it.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <label className="text-xs font-medium text-gray-700">
              Timetable name
              <input
                required
                maxLength={100}
                value={createForm.group_name}
                onChange={(event) =>
                  setCreateForm((value) => ({
                    ...value,
                    group_name: event.target.value,
                  }))
                }
                className="mt-1.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="e.g. Upper Primary 4, 5 and 6"
              />
            </label>
            <label className="text-xs font-medium text-gray-700">
              Academic year
              <select
                required
                value={createForm.academic_year_id}
                onChange={(event) =>
                  setCreateForm((value) => ({
                    ...value,
                    academic_year_id: event.target.value,
                  }))
                }
                className="mt-1.5 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">Academic year</option>
                {years.map((year) => (
                  <option key={year.id} value={year.id}>
                    {year.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-700">
                Select classes
              </span>
              <span className="text-xs text-gray-400">
                {createForm.class_ids.length} selected
              </span>
            </div>
            <div className="grid max-h-72 gap-2 overflow-y-auto rounded-xl border border-gray-100 bg-gray-50/50 p-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {classes.map((klass) => {
                const selected = createForm.class_ids.includes(
                  String(klass.id),
                );
                return (
                  <label
                    key={klass.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-2.5 py-2 text-[11px] transition-all hover:-translate-y-0.5 sm:text-xs ${
                      selected
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-gray-200 bg-white text-gray-600"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleCreateClass(klass.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600"
                    />
                    <span className="font-medium">{klass.name}</span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !createForm.class_ids.length}
              className="rounded-lg bg-primary-600 px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-primary-700 hover:shadow-md disabled:opacity-50"
            >
              {saving ? "Creating..." : "Create timetable"}
            </button>
          </div>
        </form>
      )}

      {selectedGroup && (
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-base font-bold text-gray-800 sm:text-lg">
              {selectedGroup.group_name}
            </h2>
            <p className="text-[11px] text-gray-500 sm:text-xs">
              {selectedGroup.timetables.length} class
              {selectedGroup.timetables.length === 1 ? "" : "es"}:{" "}
              {selectedGroup.timetables
                .map((item) => item.class?.name)
                .filter(Boolean)
                .join(", ")}
            </p>
          </div>
        </div>
      )}

      {selectedGroup && (
        <p className="rounded-xl bg-blue-50 px-3 py-2 text-center text-xs text-blue-600 lg:hidden">
          Swipe left or right to view every period.
        </p>
      )}

      <div className="max-h-[72vh] overflow-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-sm [scrollbar-width:thin]">
        <table className="min-w-max border-collapse text-[9px] sm:text-[10px] lg:text-[11px]">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-40 min-w-[68px] border-b border-r bg-gray-50 px-2 py-2 text-left sm:min-w-[78px]">
                Day
              </th>
              <th className="sticky left-[68px] top-0 z-40 min-w-[88px] border-b border-r bg-gray-50 px-2 py-2 text-left sm:left-[78px] sm:min-w-[102px]">
                Class
              </th>
              {sortedPeriods.map((period) => (
                <th
                  key={period.id}
                  className={`sticky top-0 z-30 border-b border-r px-2 py-1.5 text-center shadow-[0_1px_0_rgba(0,0,0,0.04)] ${
                    isBreakOrLunch(period)
                      ? "min-w-[66px] bg-amber-100 sm:min-w-[72px]"
                      : "min-w-[112px] bg-gray-50 sm:min-w-[126px]"
                  }`}
                >
                  <div
                    className={`font-semibold ${
                      isBreakOrLunch(period)
                        ? "uppercase tracking-wider text-amber-800"
                        : "text-gray-700"
                    }`}
                  >
                    {isBreakOrLunch(period) ? period.period_type : period.name}
                  </div>
                  <div className="mt-1 font-normal text-gray-400">
                    {String(period.start_time).slice(0, 5)}–
                    {String(period.end_time).slice(0, 5)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teachingDays.flatMap((day, dayIndex) =>
              displayedTimetables.map((timetable, classIndex) => (
                <tr
                  key={`${day}-${timetable.id}`}
                  className="border-b transition-colors hover:bg-gray-50/50"
                >
                  {classIndex === 0 && (
                    <td
                      rowSpan={displayedTimetables.length}
                      className="sticky left-0 z-20 border-r bg-primary-50 px-2 py-2 text-center align-middle text-[10px] font-bold text-primary-700 sm:text-xs"
                    >
                      {day}
                    </td>
                  )}
                  <td className="sticky left-[68px] z-20 border-r bg-white px-2 py-2 font-semibold text-gray-700 sm:left-[78px]">
                    {timetable.class?.name || timetable.title}
                  </td>
                  {sortedPeriods.map((period) => {
                    const entry = entryMap.get(
                      `${timetable.id}:${day}:${period.id}`,
                    );

                    const lockedPeriod = !isTeachingPeriod(period);
                    const mergePeriodColumn = isBreakOrLunch(period);
                    const firstTimetableRow =
                      dayIndex === 0 && classIndex === 0;

                    if (mergePeriodColumn && !firstTimetableRow) {
                      return null;
                    }

                    return (
                      <td
                        key={period.id}
                        rowSpan={
                          mergePeriodColumn ? timetableRowCount : undefined
                        }
                        className={`border-r ${
                          mergePeriodColumn
                            ? "bg-amber-50 p-0 align-middle"
                            : "p-1.5 align-top"
                        }`}
                      >
                        {mergePeriodColumn ? (
                          <div className="flex h-full min-h-[150px] items-center justify-center px-2 py-4">
                            <div
                              className="flex items-center gap-3 whitespace-nowrap font-bold uppercase tracking-[0.22em] text-amber-700"
                              style={{
                                writingMode: "vertical-rl",
                                transform: "rotate(180deg)",
                              }}
                            >
                              <span>{period.period_type}</span>
                              <span className="text-[10px] font-medium tracking-normal text-amber-500">
                                {String(period.start_time).slice(0, 5)}–
                                {String(period.end_time).slice(0, 5)}
                              </span>
                            </div>
                          </div>
                        ) : lockedPeriod ? (
                          <div className="flex min-h-[56px] items-center justify-center rounded-md bg-amber-50 p-1.5 text-center font-semibold text-amber-700 sm:min-h-[62px]">
                            {period.name}
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={!canEdit}
                            onClick={() => openLesson(timetable, day, period)}
                            className={`min-h-[56px] w-full rounded-md p-1.5 text-left transition-all duration-200 sm:min-h-[62px] sm:p-2 ${
                              entry
                                ? "border border-primary-100 bg-primary-50 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-sm"
                                : "border border-dashed border-gray-200 text-gray-300 hover:border-primary-300 hover:bg-primary-50/40 hover:text-primary-500"
                            }`}
                          >
                            {entry ? (
                              <>
                                <div className="text-[9px] font-semibold leading-tight text-gray-800 sm:text-[10px] lg:text-[11px]">
                                  {entry.subject?.name || "Lesson"}
                                </div>
                                <div className="mt-0.5 text-[8px] leading-tight text-gray-500 sm:text-[9px] lg:text-[10px]">
                                  {teacherLabel(entry.teacher)}
                                </div>
                                {entry.room && (
                                  <div className="mt-0.5 text-[8px] text-gray-400 sm:text-[9px]">
                                    {entry.room}
                                  </div>
                                )}
                              </>
                            ) : (
                              <span>{canEdit ? "Click to add" : "Free"}</span>
                            )}
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              )),
            )}
          </tbody>
        </table>
      </div>

      {!displayedTimetables.length && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-12 text-center text-sm text-gray-500">
          No class timetables exist for the selected academic year.
        </div>
      )}

      {lesson.timetableId && canEdit && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-[2px] sm:items-center sm:p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !saving) {
              setLesson(blankLesson);
            }
          }}
        >
          <form
            onSubmit={saveLesson}
            className="max-h-[92vh] w-full max-w-lg space-y-4 overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-2xl sm:p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-gray-800">
                  {lesson.entryId ? "Edit lesson" : "Add lesson"}
                </h2>
                <p className="text-sm text-gray-500">{lesson.day_of_week}</p>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => setLesson(blankLesson)}
                className="rounded-full p-2 transition hover:bg-gray-100 disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6 text-gray-500" />
              </button>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              Subject <span className="text-red-500">*</span>
              <select
                value={lesson.subject_id}
                onChange={(event) =>
                  setLesson((value) => ({
                    ...value,
                    subject_id: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">No subject</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Teacher <span className="text-red-500">*</span>
              <select
                value={lesson.teacher_id}
                onChange={(event) =>
                  setLesson((value) => ({
                    ...value,
                    teacher_id: event.target.value,
                  }))
                }
                className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              >
                <option value="">No teacher</option>
                {teachers.map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>
                    {teacherLabel(teacher)}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Room
              <input
                value={lesson.room}
                onChange={(event) =>
                  setLesson((value) => ({ ...value, room: event.target.value }))
                }
                placeholder="Room (optional)"
                className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Remarks
              <input
                value={lesson.remarks}
                onChange={(event) =>
                  setLesson((value) => ({
                    ...value,
                    remarks: event.target.value,
                  }))
                }
                placeholder="Remarks (optional)"
                className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </label>
            <div className="flex flex-col-reverse justify-between gap-2 border-t border-gray-100 pt-4 sm:flex-row">
              {lesson.entryId ? (
                <button
                  type="button"
                  onClick={deleteLesson}
                  disabled={saving}
                  className="flex items-center justify-center gap-2 rounded-xl border border-red-100 px-4 py-2.5 text-red-600 transition hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4" /> Remove
                </button>
              ) : (
                <span />
              )}
              <button
                type="submit"
                disabled={saving}
                className="rounded-xl bg-primary-600 px-5 py-2.5 font-semibold text-white transition hover:bg-primary-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save lesson"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}